import { useEffect, useState } from "react";

import { Button } from "../../../Components/Buttons";
import { FormInput, FormSection, FormSelect, FormSwitch } from "../../../Components/Form";
import { ToastMessage } from "../../../Components/Utils";
import { useFetchLatestSmtpConfigQuery, useSaveSmtpConfigMutation } from "../../../stores/api/smtpConfig.api";

const ENCRYPTION_OPTIONS = [
  { value: "tls",  label: "TLS" },
  { value: "ssl",  label: "SSL" },
  { value: "none", label: "None" },
];

const EMPTY = {
  host: "",
  port: "",
  username: "",
  password: "",
  encryption: "tls",
  from_address: "",
  from_name: "",
  enabled: true,
};

const SmtpSettings = () => {
  const [formData, setFormData] = useState(EMPTY);
  const { data: fetched, isLoading } = useFetchLatestSmtpConfigQuery();
  const [saveConfig, { isLoading: isSaving }] = useSaveSmtpConfigMutation();

  useEffect(() => {
    if (fetched) {
      setFormData({
        host: fetched.host || "",
        port: fetched.port != null ? String(fetched.port) : "",
        username: fetched.username || "",
        password: fetched.password || "",
        encryption: fetched.secure ? "tls" : "none",
        from_address: fetched.from_email || "",
        from_name: fetched.from_name || "",
        enabled: true,
      });
    }
  }, [fetched]);

  const setField = (name, value) => setFormData(prev => ({ ...prev, [name]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      host: formData.host,
      port: Number(formData.port),
      username: formData.username,
      password: formData.password,
      secure: formData.encryption !== "none",
      from_email: formData.from_address,
      from_name: formData.from_name,
    };
    try {
      await saveConfig(payload).unwrap();
      ToastMessage({ type: "success", title: "Success", message: "SMTP settings saved!" });
    } catch (err) {
      ToastMessage({ type: "error", title: "Error!", message: err?.data?.message || err?.message || "Something went wrong" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mx-auto dark:bg-slate-900 rounded-lg p-6 space-y-5">
      <FormSection title="SMTP Server">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="SMTP Host" name="host" value={formData.host}
            onChange={(val) => setField("host", val)}
            placeholder="e.g. smtp.gmail.com"
          />
          <FormInput
            label="SMTP Port" name="port" value={formData.port}
            onChange={(val) => setField("port", val)}
            placeholder="e.g. 587"
          />
          <FormInput
            label="Username" name="username" value={formData.username}
            onChange={(val) => setField("username", val)}
            placeholder="your@email.com"
          />
          <FormInput
            label="Password" name="password" type="password" value={formData.password}
            onChange={(val) => setField("password", val)}
            placeholder="SMTP password or app key"
          />
          <FormSelect
            label="Encryption"
            options={ENCRYPTION_OPTIONS}
            value={formData.encryption}
            onChange={(val) => setField("encryption", val ?? "tls")}
          />
          <div className="flex flex-col gap-1 justify-end pb-1">
            <FormSwitch
              label="Enable SMTP"
              checked={formData.enabled}
              onChange={(val) => setField("enabled", val)}
            />
          </div>
        </div>
      </FormSection>

      <FormSection title="Sender Identity">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="From Address" name="from_address" value={formData.from_address}
            onChange={(val) => setField("from_address", val)}
            placeholder="no-reply@yourdomain.com"
          />
          <FormInput
            label="From Name" name="from_name" value={formData.from_name}
            onChange={(val) => setField("from_name", val)}
            placeholder="e.g. Directorate Of MD&EHS Rehabilitation Department Government Of Sindh"
          />
        </div>
      </FormSection>

      <div className="flex items-center justify-end gap-3 pt-2">
        <Button
          type="submit" loading={isSaving} disabled={isSaving}
          className="!bg-green-500 !text-white hover:!bg-green-600"
          loadingText="Saving..."
        >
          Save Settings
        </Button>
      </div>
    </form>
  );
};

export default SmtpSettings;